import {
  acceptHotItemStore,
  defineItemStore
} from "@editor/items/itemStore.store";

import type { StakeholderItem } from "./StakeholderItem";

export const stakeholderItemStructure: StakeholderItem = {
  type: "stakeholder",
  id: 0,
  uid: "stakeholder_0",

  title: "",
  description: "",

  indexIds: [],
  targetIds: [],
  actionIds: [],
  resourceIds: [],

  docs: {},
  createdAt: -1
};

export const useStakeholderStore = defineItemStore(
  "model/stakeholders",
  stakeholderItemStructure,
  {
    getters: {
      /**
       * Get all the stakeholders, grouped by target id.
       */
      byTarget(state) {
        const orderedByTarget: { [targetId: number]: StakeholderItem[] } = {};
        for (const stakeholderId in state.items) {
          const stakeholder = state.items[stakeholderId];
          for (const targetId of stakeholder.targetIds) {
            if (orderedByTarget[targetId] == null)
              orderedByTarget[targetId] = [];
            orderedByTarget[targetId].push(stakeholder);
          }
        }
        return orderedByTarget;
      },

      byAction(state) {
        const orderedByAction: { [actionId: number]: StakeholderItem[] } = {};
        for (const stakeholderId in state.items) {
          const stakeholder = state.items[stakeholderId];
          for (const actionId of stakeholder.actionIds) {
            if (orderedByAction[actionId] == null)
              orderedByAction[actionId] = [];
            orderedByAction[actionId].push(stakeholder);
          }
        }
        return orderedByAction;
      },

      byResource(state) {
        const orderedByResource: { [resourceId: number]: StakeholderItem[] } =
          {};
        for (const stakeholderId in state.items) {
          const stakeholder = state.items[stakeholderId];
          for (const resourceId of stakeholder.resourceIds) {
            if (orderedByResource[resourceId] == null)
              orderedByResource[resourceId] = [];
            orderedByResource[resourceId].push(stakeholder);
          }
        }
        return orderedByResource;
      },

      byIndex(state) {
        const orderedByIndex: { [indexId: number]: StakeholderItem[] } = {};
        for (const stakeholderId in state.items) {
          const stakeholder = state.items[stakeholderId];
          for (const indexId of stakeholder.indexIds) {
            if (orderedByIndex[indexId] == null) orderedByIndex[indexId] = [];
            orderedByIndex[indexId].push(stakeholder);
          }
        }
        return orderedByIndex;
      },

      byItemUid(): { [uid: string]: StakeholderItem[] } {
        const orderedByItemUid: { [uid: string]: StakeholderItem[] } = {};
        for (const stakeholder of this.orderedItems) {
          for (const indexId of stakeholder.indexIds) {
            const uid = `index_${indexId}`;
            if (orderedByItemUid[uid] == null) orderedByItemUid[uid] = [];
            orderedByItemUid[uid].push(stakeholder);
          }
          for (const targetId of stakeholder.targetIds) {
            const uid = `target_${targetId}`;
            if (orderedByItemUid[uid] == null) orderedByItemUid[uid] = [];
            orderedByItemUid[uid].push(stakeholder);
          }
          for (const actionId of stakeholder.actionIds) {
            const uid = `action_${actionId}`;
            if (orderedByItemUid[uid] == null) orderedByItemUid[uid] = [];
            orderedByItemUid[uid].push(stakeholder);
          }
          for (const resourceId of stakeholder.resourceIds) {
            const uid = `resource_${resourceId}`;
            if (orderedByItemUid[uid] == null) orderedByItemUid[uid] = [];
            orderedByItemUid[uid].push(stakeholder);
          }
        }
        return orderedByItemUid;
      }
    },
    actions: {
      addTarget({ targetId, id }: { targetId: number; id: number }) {
        this.items[id].targetIds.push(targetId);
      },
      removeTarget({ id, targetId }: { targetId: number; id: number }) {
        const newIds = this.items[id].targetIds.slice();
        newIds.splice(newIds.indexOf(targetId), 1);
        this.items[id].targetIds = newIds;
      },

      addAction({ actionId, id }: { actionId: number; id: number }) {
        this.items[id].actionIds.push(actionId);
      },
      removeAction({ id, actionId }: { actionId: number; id: number }) {
        const newIds = this.items[id].actionIds.slice();
        newIds.splice(newIds.indexOf(actionId), 1);
        this.items[id].actionIds = newIds;
      },

      addResource({ resourceId, id }: { resourceId: number; id: number }) {
        this.items[id].resourceIds.push(resourceId);
      },
      removeResource({ id, resourceId }: { resourceId: number; id: number }) {
        const newIds = this.items[id].resourceIds.slice();
        newIds.splice(newIds.indexOf(resourceId), 1);
        this.items[id].resourceIds = newIds;
      },

      addIndex({ indexId, id }: { indexId: number; id: number }) {
        this.items[id].indexIds.push(indexId);
      },
      removeIndex({ id, indexId }: { indexId: number; id: number }) {
        const newIds = this.items[id].indexIds.slice();
        newIds.splice(newIds.indexOf(indexId), 1);
        this.items[id].indexIds = newIds;
      }
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHotItemStore(useStakeholderStore, import.meta.hot)
  );
}
